import moment from 'moment'
import { DateValue } from '../components/fields/Date/DateRangePicker'

const isValidDateRange = (dateRange?: DateValue | null | Array<Date | null>): dateRange is [Date, Date] => {
  if (!dateRange) return false
  const copy = [...dateRange]
  if (copy[0] && copy[1] && moment(copy[0]).isValid() && moment(copy[1]).isValid()) {
    return true
  }

  return false
}
const isEqualDateRange = (dateRangeA?: DateValue | null | Array<Date | null>, dateRangeB?: DateValue | null | Array<Date | null>): boolean => {
  if (!isValidDateRange(dateRangeA) || !isValidDateRange(dateRangeB)) return false
  const startRangeSimilar = moment(dateRangeA[0]).format('YYYY-MM-DD') === moment(dateRangeB[0]).format('YYYY-MM-DD')
  const endRangeSimilar = moment(dateRangeA[1]).format('YYYY-MM-DD') === moment(dateRangeB[1]).format('YYYY-MM-DD')

  return startRangeSimilar && endRangeSimilar
}

function removeTimeOffset(isoString: string): Date {
  // Parse the ISO string into a Date object
  const date = moment(isoString).toDate()

  // Get the UTC components of the date
  const utcYear = date.getUTCFullYear()
  const utcMonth = String(date.getUTCMonth() + 1).padStart(2, '0') // Months are 0-based
  const utcDay = String(date.getUTCDate()).padStart(2, '0')
  const utcHours = String(date.getUTCHours()).padStart(2, '0')
  const utcMinutes = String(date.getUTCMinutes()).padStart(2, '0')
  const utcSeconds = String(date.getUTCSeconds()).padStart(2, '0')

  // Construct a new ISO string in UTC without timezone offset
  const utcIsoString = `${utcYear}-${utcMonth}-${utcDay}T${utcHours}:${utcMinutes}:${utcSeconds}`

  return moment(utcIsoString).toDate()
}

export function dateToUrlIso(date: Date): string {
  return toUTCDate(date).toISOString()
}

/**
 * Converts a given Date object to a UTC Date object.
 * @param {Date} date - The Date object to convert.
 * @returns {Date} - A Date object representing the same date and time in UTC.
 */
export function toUTCDate(date: Date) {
  // Create a new Date object using the UTC components of the provided date
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()))
}

export const isoToDate = (payload: string): Date | null => {
  try {
    if (!payload || typeof payload !== 'string') {
      return null
    }

    const payloadSplit = payload.split('-')

    if (payloadSplit.length < 3) {
      return null
    }

    const payloadYear = parseInt(payloadSplit[0])
    const payloadIndexedMonth = parseInt(payloadSplit[1]) - 1
    const payloadDate = parseInt(payloadSplit[2].split('T')[0])

    if (isNaN(payloadYear) || isNaN(payloadIndexedMonth) || isNaN(payloadDate)) {
      return null
    }

    const date = new Date(payloadYear, payloadIndexedMonth, payloadDate, 0, 0, 0, 0)

    if (isNaN(date.getTime())) {
      return null
    }

    return date
  } catch (error) {
    console.error('Error parsing ISO date:', error)
    return null
  }
}

export const utcDateToLocalMoment = (date: Date | string | moment.Moment) => {
  return moment.utc(date).local()
}

export const localDateToUtc = (date: Date | string | moment.Moment) => {
  return moment(date).utc().toISOString()
}

const MEXICO_TIMEZONES = ['America/Mexico_City', 'America/Cancun', 'America/Merida', 'America/Monterrey', 'America/Matamoros', 'America/Mazatlan', 'America/Chihuahua', 'America/Ojinaga', 'America/Hermosillo', 'America/Tijuana', 'America/Bahia_Banderas']

export const getBrowserTimezone = (): string => {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  if (MEXICO_TIMEZONES.includes(browserTimezone)) {
    return 'America/Mexico_City'
  }

  return browserTimezone
}

/**
 * Parses a date-only string (e.g. "2026-02-01") from the API into a local Date
 * without timezone shift. Use this for fields stored as DATE (not TIMESTAMP).
 */
export const parseDateOnly = (value: string): Date => {
  return moment.utc(value).toDate()
}

/**
 * Formats a local Date to "YYYY-MM-DD" for sending to the API.
 * Use this for fields stored as DATE (not TIMESTAMP).
 */
export const formatDateOnly = (value: Date): string => {
  return moment(value).format('YYYY-MM-DD')
}

/**
 * Formats a date-only string from the API to a display string (DD/MM/YYYY)
 * without timezone shift.
 */
export const displayDateOnly = (value: string, format: string = 'DD/MM/YYYY'): string => {
  return moment.utc(value).format(format)
}

/**
 * Returns the formatted expense date.
 * When a receipt_date exists (from OCR), it's displayed as UTC to avoid timezone shift.
 * Otherwise, the expense date is displayed in local time (entered by the user from the dashboard).
 */
export const displayExpenseDate = (expenseDate: Date | string, receiptDate?: Date | string | null, format: string = 'DD/MM/YYYY'): string => {
  if (receiptDate) {
    return moment.utc(receiptDate).format(format)
  }
  return moment(expenseDate).format(format)
}

export { isValidDateRange, isEqualDateRange, removeTimeOffset }
